import { FC, useEffect, useState } from 'react'
import TimelineControls from '../common/TimelineControls'
import { formatTime } from '../PlayerUtils'

export const useSampleTimelineControls = (audio: HTMLAudioElement) => {
  const duration = audio?.duration
  const [currentTime, setCurrentTime] = useState(audio?.currentTime)
  const [wasPlaying, setWasPlaying] = useState(false)

  const updateAnimation = () => {
    setCurrentTime(audio.currentTime)
    if (!audio.paused) requestAnimationFrame(updateAnimation)
  }

  const updateCurrentTime = () => {
    if (!audio.paused) return
    updateAnimation()
  }

  const handleChange = ([value]: [number]) => {
    audio.currentTime = value / 1000
    audio.pause()
  }

  const handleChangeEnd = async () => {
    if (wasPlaying) await audio.play()
  }

  const handleChangeStart = () => {
    if (audio.paused) setWasPlaying(false)
    else setWasPlaying(true)
  }

  useEffect(() => {
    if (!audio) return
    if (!audio.paused) updateAnimation()
    setCurrentTime(audio.currentTime)
    audio.addEventListener('timeupdate', updateCurrentTime)
    audio.addEventListener('play', updateAnimation)
    return () => {
      audio.removeEventListener('timeupdate', updateCurrentTime)
      audio.removeEventListener('play', updateAnimation)
    }
  }, [audio])

  return { duration, currentTime, handleChange, handleChangeEnd, handleChangeStart }
}

export interface AudioControlsProps {
  audio: HTMLAudioElement
  hideLabels?: boolean
  style?: {}
}

const SampleTimelineControls: FC<AudioControlsProps> = ({ audio, hideLabels, style }) => {
  const { currentTime, duration, handleChange, handleChangeEnd, handleChangeStart } =
    useSampleTimelineControls(audio)
  const formattedDuration = isNaN(duration) ? '00:00' : formatTime(duration)
  const formattedCurrentTime = formatTime(currentTime)
  return (
    <TimelineControls
      value={currentTime * 1000}
      max={duration * 1000}
      onValueChange={handleChange}
      leftLabel={hideLabels ? null : formattedCurrentTime}
      rightLabel={hideLabels ? null : formattedDuration}
      onSlideEnd={handleChangeEnd}
      onSlideStart={handleChangeStart}
      style={style}
    />
  )
}

export default SampleTimelineControls
